import { useState, type ReactNode } from 'react'
import { Sidebar } from './sidebar'
import { Header } from './header'
import { MobileNav } from './mobile-nav'
import { useMfaStatus } from '@/features/auth/hooks/use-mfa-status'
import { MfaGracePeriod } from '@/features/auth/components/mfa-grace-period'
import { MfaEnrollment } from '@/features/auth/components/mfa-enrollment'
import { Modal } from '@/components/ui/modal'
import { useMaintenanceMode } from '@/hooks/use-maintenance-mode'
import { usePageTracker } from '@/hooks/use-page-tracker'
import { Breadcrumbs } from './breadcrumbs'
import { TrialBanner } from '@/features/payments/components/trial-banner'
import { AlertTriangle } from 'lucide-react'

interface AppShellProps {
  children: ReactNode
}

const MAX_GRACE_DAYS = 30
const MAX_GRACE_LOGINS = 5

export function AppShell({ children }: AppShellProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    try {
      return localStorage.getItem('zero:sidebar-pinned') === 'true'
    } catch {
      return false
    }
  })
  const [showMfaSetup, setShowMfaSetup] = useState(false)
  const {
    mfaEnabled,
    graceStartDate,
    loginCount,
    isGracePeriodExpired,
    loading: mfaLoading,
  } = useMfaStatus()
  const { enabled: maintenanceEnabled, endTime: maintenanceEndTime } = useMaintenanceMode()
  usePageTracker()

  const showGracePeriodWarning = !mfaLoading && !mfaEnabled && graceStartDate

  const handleTogglePin = () => {
    const next = !sidebarPinned
    setSidebarPinned(next)
    try {
      localStorage.setItem('zero:sidebar-pinned', String(next))
    } catch {
      /* ignore */
    }
    // Close mobile nav when pinning
    if (next) setMobileNavOpen(false)
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-[#0a0a0a]">
      {/* Skip navigation link — WCAG 2.1 Level AA */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-zero-600 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white focus:shadow-lg focus:outline-none"
      >
        Skip to main content
      </a>
      <Sidebar pinned={sidebarPinned} onTogglePin={handleTogglePin} />
      <MobileNav
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        onPin={handleTogglePin}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header onMenuToggle={() => setMobileNavOpen(true)} sidebarPinned={sidebarPinned} />
        <main
          id="main-content"
          role="main"
          tabIndex={-1}
          className="flex-1 overflow-y-auto p-4 lg:p-6 focus:outline-none"
        >
          <Breadcrumbs />
          <TrialBanner />
          {maintenanceEnabled && (
            <div className="mb-4">
              <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Maintenance mode is active.</span> Non-admin users
                  are blocked from signing in.
                  {maintenanceEndTime && (
                    <span>
                      {' '}
                      Auto-off scheduled:{' '}
                      {new Date(maintenanceEndTime).toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}
          {showGracePeriodWarning && (
            <div className="mb-4">
              <MfaGracePeriod
                gracePeriodStart={graceStartDate!}
                loginCount={loginCount}
                maxLogins={MAX_GRACE_LOGINS}
                maxDays={MAX_GRACE_DAYS}
                onSetupMfa={() => setShowMfaSetup(true)}
              />
            </div>
          )}
          {children}
        </main>
      </div>

      {/* MFA enrollment modal — triggered by grace period warning/blocker */}
      {showMfaSetup && (
        <Modal
          open
          onClose={isGracePeriodExpired ? () => {} : () => setShowMfaSetup(false)}
          size="lg"
        >
          <MfaEnrollment
            onComplete={() => setShowMfaSetup(false)}
            onCancel={isGracePeriodExpired ? undefined : () => setShowMfaSetup(false)}
          />
        </Modal>
      )}
    </div>
  )
}
